import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Box,
  Typography,
  Icon,
  Paper,
  Tooltip
} from '@mui/material';

// Available icons for categories
const availableIcons = [
  'description', 'credit_card', 'folder', 'settings', 'account_balance',
  'business', 'gavel', 'badge', 'flight', 'engineering', 'receipt',
  'receipt_long', 'assessment', 'percent', 'calculate', 'work',
  'apartment', 'home', 'store', 'local_hospital'
];

// Color presets
const colorPresets = [
  '#4CAF50', '#2196F3', '#9C27B0', '#FF9800', '#FF5722',
  '#E91E63', '#00BCD4', '#795548', '#607D8B', '#3F51B5'
];

/**
 * Category Form Dialog
 * Add/Edit dialog for document categories
 * Uses mock data during UI-First phase
 */
const CategoryFormDialog = ({ open, onClose, onSave, category, verticalId }) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    nameAr: '',
    icon: 'folder',
    colour: '#4CAF50',
    isActive: true
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isEdit = !!category;

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (category) {
        setFormData({
          code: category.code || '',
          name: category.name || '',
          nameAr: category.nameAr || '',
          icon: category.icon || 'folder',
          colour: category.colour || category.color || '#4CAF50',
          isActive: category.isActive !== undefined ? category.isActive : true
        });
      } else {
        setFormData({
          code: '',
          name: '',
          nameAr: '',
          icon: 'folder',
          colour: '#4CAF50',
          isActive: true
        });
      }
      setErrors({});
    }
  }, [open, category]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleIconSelect = (icon) => {
    setFormData(prev => ({ ...prev, icon }));
  };

  const handleColorSelect = (color) => {
    setFormData(prev => ({ ...prev, colour: color }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    } else if (!/^[A-Z_]+$/.test(formData.code)) {
      newErrors.code = 'Code must be uppercase letters and underscores only';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEdit ? 'Edit Category' : 'Add New Category'}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Code */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Code"
              value={formData.code}
              onChange={handleChange('code')}
              error={!!errors.code}
              helperText={errors.code || 'e.g., LICENSE, GOV_CARD'}
              fullWidth
              required
              disabled={isEdit}
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />
          </Grid>

          {/* Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              required
            />
          </Grid>

          {/* Name Arabic */}
          <Grid item xs={12}>
            <TextField
              label="Name (Arabic)"
              value={formData.nameAr}
              onChange={handleChange('nameAr')}
              fullWidth
              dir="rtl"
              placeholder="الاسم بالعربية"
              helperText="Display name in Arabic (optional)"
            />
          </Grid>

          {/* Icon Selection */}
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Icon
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {availableIcons.map((icon) => (
                  <Tooltip key={icon} title={icon}>
                    <Box
                      onClick={() => handleIconSelect(icon)}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        cursor: 'pointer',
                        backgroundColor: formData.icon === icon ? 'primary.light' : 'transparent',
                        border: formData.icon === icon ? '2px solid' : '2px solid transparent',
                        borderColor: formData.icon === icon ? 'primary.main' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <Icon color={formData.icon === icon ? 'primary' : 'action'}>
                        {icon}
                      </Icon>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Color Selection */}
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Color
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {colorPresets.map((color) => (
                <Tooltip key={color} title={color}>
                  <Box
                    onClick={() => handleColorSelect(color)}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      backgroundColor: color,
                      cursor: 'pointer',
                      border: formData.colour === color ? '3px solid' : '3px solid transparent',
                      borderColor: formData.colour === color ? 'grey.800' : 'transparent',
                      '&:hover': {
                        transform: 'scale(1.1)'
                      },
                      transition: 'transform 0.2s'
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
            <TextField
              size="small"
              value={formData.colour}
              onChange={handleChange('colour')}
              placeholder="#XXXXXX"
              sx={{ mt: 1, width: 120 }}
              inputProps={{ maxLength: 7 }}
            />
          </Grid>

          {/* Active Status */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleChange('isActive')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Active</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Inactive categories won't be available for document upload
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryFormDialog;
